import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { myFetch } from '@/commons/fetch';
import { Notification as NotificationType } from '@/users/components/NotificationCenter/types';

export interface NotificationsState {
  notifications: NotificationType[];
  isLoading: boolean;
  hasFetched: boolean;
  pagination: {
    total: number;
    limit: number;
    skip: number;
    hasMore: boolean;
  };
}

const initialState: NotificationsState = {
  notifications: [],
  isLoading: false,
  hasFetched: false,
  pagination: {
    total: 0,
    limit: 50,
    skip: 0,
    hasMore: false,
  },
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({
    skip = 0,
    limit = 50,
    force = false,
  }: {
    skip?: number;
    limit?: number;
    force?: boolean;
  }) => {
    const response = await myFetch(
      `/notifications?limit=${limit}&skip=${skip}`
    );
    return response;
  },
  {
    condition: (arg, { getState }) => {
      const state = getState() as { notifications: NotificationsState };
      const { isLoading, hasFetched } = state.notifications;

      // If force is true, allow the fetch (for refresh)
      if (arg.force) {
        return !isLoading;
      }

      // For initial fetch (skip=0), prevent if already fetched or loading
      if (arg.skip === 0) {
        return !hasFetched && !isLoading;
      }

      // For pagination (skip > 0), only prevent if already loading
      return !isLoading;
    },
  }
);

export const updateNotificationReadStatus = createAsyncThunk(
  'notifications/updateReadStatus',
  async ({
    notificationId,
    read,
  }: {
    notificationId: string;
    read: boolean;
  }) => {
    const response = await myFetch(`/notifications/${notificationId}`, {
      method: 'PATCH',
      body: JSON.stringify({ read }),
    });
    return { notificationId, read, response };
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    resetNotifications: (state) => {
      state.notifications = [];
      state.hasFetched = false;
      state.pagination = initialState.pagination;
    },
    setHasFetched: (state, action: { payload: boolean }) => {
      state.hasFetched = action.payload;
    },
    addNotification: (state, action: { payload: NotificationType }) => {
      // Add notification at the beginning of the array
      state.notifications.unshift(action.payload);
      // Update pagination total
      state.pagination.total += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.hasFetched = true;
        if (action.payload.success) {
          if (action.meta.arg.skip === 0) {
            state.notifications = action.payload.data;
          } else {
            state.notifications = [
              ...state.notifications,
              ...action.payload.data,
            ];
          }
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.isLoading = false;
        state.hasFetched = true;
      })
      .addCase(updateNotificationReadStatus.fulfilled, (state, action: any) => {
        // Update the notification in the store
        const notification = state.notifications.find(
          (n) => n.id === action.payload.notificationId
        );
        if (notification) {
          notification.read = action.payload.read;
        }
      });
  },
});

export const { resetNotifications, setHasFetched, addNotification } =
  notificationsSlice.actions;

export default notificationsSlice.reducer;
